import React, { useRef } from "react"
import type { Item } from "./Nestable"
import "./NestableItem.scoped.scss"
import { useDispatch } from "react-redux";
import { updateCreateRcStepCollapsed, updateStepCollapsed } from "store/manageSW/manageSWActions";
import dragIcon from "media/icons/dls/drag.svg";

type DragState = {
    path: number[];
    position: "before" | "after" | "inside";
    height?: number;
}

type Props = {
    item: Item;
    depth: number;
    path: number[];
    renderItem: any;
    childrenProps: string;
    onItemPointerDown: (data: { item: Item; path: number[]; height: number; clientX: number; clientY: number }) => void;
    onToggle: (path: number[]) => void;
    dragState: DragState | null;
    renderCollapseIcon: any;
    displayAll: boolean;
    isStepList?: boolean;
}

export const NestableItem: React.FC<Props> = ({
    item,
    depth,
    path,
    renderItem,
    childrenProps,
    onItemPointerDown,
    onToggle,
    dragState,
    renderCollapseIcon,
    displayAll,
    isStepList
}) => {
    const dispatch = useDispatch();
    const hasChildren = item[childrenProps] && item[childrenProps].length > 0;
    const itemRef = useRef<HTMLLIElement>(null);

    const isBeforeActive = dragState?.position === "before" && dragState.path.join(",") === path.join(",");

    const isAfterActive = dragState?.position === "after" && dragState.path.join(",") === path.join(",");

    const isInsideActive = dragState?.position === "inside" && dragState.path.join(",") === path.join(",");

    const activeHeight = dragState?.height ?? 0;

    // pointer-based drag (instead of native HTML5 DnD) so wheel scrolling keeps working mid-drag
    const handlePointerDown = (e: React.PointerEvent) => {
        if (e.button !== 0) return;

        const height = itemRef.current?.getBoundingClientRect().height || 0;

        onItemPointerDown({ item, path, height, clientX: e.clientX, clientY: e.clientY });
    }

    const toggleCollapse = () => {
        onToggle(path)
        if (isStepList) {
            dispatch(updateStepCollapsed({ stepId: item.id.length > 3 ? item.id : Number(item.id), collapsed: !item.collapsed }))
        } else {
            dispatch(updateCreateRcStepCollapsed({ stepId: item.id.length > 3 ? item.id : Number(item.id), collapsed: !item.collapsed }))
        }
    }

    return (
        <li ref={itemRef} className="nestable-item">
            <div className={`nestable-dropzone before ${isBeforeActive ? "active" : ""}`}
                data-nestable-path={path.join(",")}
                data-nestable-position="before"
                style={{ height: isBeforeActive ? activeHeight : undefined }}
            >
                {isBeforeActive && <div className="nestable-drop-indicator" />}
            </div>

            <div className="nestable-item-content"
                data-nestable-path={path.join(",")}
                data-nestable-position="inside"
                onPointerDown={handlePointerDown}
                onDragStart={(e) => e.preventDefault()}>
                {isInsideActive && <div className="nestable-inside-indicator" />}
                {renderItem({
                    item,
                    depth,
                    hasChildren,
                    toggleCollapse,
                    collapseIcon:
                        item[childrenProps]?.length ? (
                            <span className="nestable-toggle"
                                onClick={e => {
                                    e.stopPropagation();
                                    toggleCollapse()
                                }}
                            >
                                {renderCollapseIcon({ isCollapsed: item.collapsed, id: item.id, parentGuid: item.parentGuid, sortOrder: item.sortOrder, stepId: item.stepId })}
                            </span>
                        ) : (
                            <img
                                className="icon-small"
                                alt="drag"
                                draggable={false}
                                src={dragIcon}
                            />
                        )

                })}
            </div>

            {(item.collapsed || displayAll) && hasChildren && (
                <ul className="nestable-list">
                    {item[childrenProps].map((child: Item, index: number) => (
                        <NestableItem
                            key={child.id}
                            item={child}
                            depth={depth + 1}
                            path={[...path, index]}
                            renderItem={renderItem}
                            childrenProps={childrenProps}
                            onItemPointerDown={onItemPointerDown}
                            onToggle={onToggle}
                            dragState={dragState}
                            renderCollapseIcon={renderCollapseIcon}
                            displayAll={displayAll}
                        />
                    ))}
                </ul>
            )}
            <div className={`nestable-dropzone after ${isAfterActive ? "active" : ""}`}
                data-nestable-path={path.join(",")}
                data-nestable-position="after"
                style={{ height: isAfterActive ? activeHeight : undefined }}>
                {isAfterActive && <div className="nestable-drop-indicator" />}
            </div>
        </li>
    )
}